import { assertNever } from '@framework/engine/utils/assertNever.ts';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { TileArcGISRest, TileWMS } from 'ol/source';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import { type ServerType } from 'ol/source/wms';
import XYZ from 'ol/source/XYZ';
import { Fill, RegularShape, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { type StyleFunction } from 'ol/style/Style';
import {
  DefaultMapLayerMinZoom,
  DefaultMapSymbolSize,
  type IMapLayer,
  type IMapLayerStyle,
  MapLayerType,
  MapSymbolShape,
} from '../MapLayer.ts';
import { createLayerVectorSource } from '../vector/createLayerVectorSource.ts';
import { normalizeLayerUrl, parseLayerAndOptions } from './layerUrl.ts';

// Re-export storico: molti moduli (incluso validator-api) importano le
// utility URL da questo file.
export { normalizeLayerUrl, parseLayerAndOptions } from './layerUrl.ts';

/**
 * Converte la configurazione di un layer nel layer OpenLayers
 * corrispondente.
 */
export function creteMapLayerFromConfig(layer: IMapLayer) {
  switch (layer.type) {
    case MapLayerType.OSM:
      return new TileLayer({
        source: new OSM(),
        opacity: layer.style?.opacity,
      });

    case MapLayerType.XYZ: {
      let url = normalizeLayerUrl(layer.url);
      url = url?.includes('{x}') ? url : url + '{z}/{y}/{x}';

      return new TileLayer({
        source: new XYZ({
          url,
          attributions: layer.name,
        }),
        opacity: layer.style?.opacity,
      });
    }

    case MapLayerType.ArcGISRestTile: {
      const { url, options } = parseLayerAndOptions(layer.url);
      return new TileLayer({
        source: new XYZ({
          url: `${url}/tile/{z}/{y}/{x}`,
          attributions: layer.name,
        }),
        opacity: layer.style?.opacity,
      });
    }

    case MapLayerType.WMS: {
      const {
        url,
        options: {
          serverType, // = 'geoserver', 'qgis', 'mapserver', 'carmentaserver', 'geonode'
          ...defaultOptions
        },
      } = parseLayerAndOptions(layer.url);

      const options = { LAYERS: '', ...defaultOptions };

      return new TileLayer({
        // extent: [-13884991, 2870341, -7455066, 6338219],
        source: new TileWMS({
          url,
          params: { ...options, TILED: true },
          serverType: serverType as ServerType | undefined,
          transition: 0,
        }),
      });
    }

    case MapLayerType.ArcGISRest: {
      const url = normalizeLayerUrl(layer.url);
      return new TileLayer({
        source: new TileArcGISRest({
          url,
        }),
        opacity: layer.style?.opacity,
      });
    }

    case MapLayerType.ArcGISRestVector:
    case MapLayerType.WFS: {
      // Config incompleta (es. anteprima WFS senza typeName): layer vuoto
      const source = createLayerVectorSource(layer) ?? new VectorSource();

      return new VectorLayer({
        minZoom: layer.minZoom ?? DefaultMapLayerMinZoom,
        source,
        opacity: layer.style?.opacity,
        style: createVectorLayerStyleFunction(layer),
      });
    }

    default:
      assertNever(layer.type);
  }
}

/**
 * Style function per i layer vettoriali: stile di base dalla config,
 * con etichetta per feature quando è definita `labelPropertyName`.
 * Sulle feature puntiformi l'etichetta viene spostata sotto il simbolo
 * per non sovrapporsi.
 */
function createVectorLayerStyleFunction(layer: IMapLayer): StyleFunction {
  const style = createVectorStyle(layer.style);
  const symbolRadius = layer.style?.symbol?.size ?? DefaultMapSymbolSize;

  return feature => {
    // Se non è definita una proprietà per l'etichetta, restituiamo lo
    // stile di default uguale per tutte le feature
    if (!layer.labelPropertyName) return style;

    const geometryType = feature.getGeometry()?.getType();
    const isPoint = geometryType === 'Point' || geometryType === 'MultiPoint';

    const featureStyle = style.clone();
    featureStyle.setText(
      new Text({
        fill: new Fill({
          color: 'black',
        }),
        text: feature.getProperties()?.[layer.labelPropertyName] ?? '',
        ...(isPoint && {
          offsetY: symbolRadius + 4,
          textBaseline: 'top',
        }),
      }),
    );
    return featureStyle;
  };
}

export function createVectorStyle(style: IMapLayerStyle | undefined) {
  // L'`image` viene applicata da OL solo alle feature puntiformi,
  // fill/stroke solo a poligoni e linee: lo stile è unico per il layer.
  return new Style({
    fill: new Fill({
      color: style?.fillColor,
    }),
    stroke: new Stroke({
      color: style?.strokeColor,
      width: style?.strokeWidth,
    }),
    image: createSymbolImage(style),
  });
}

/**
 * Crea l'immagine OL per il simbolo delle feature puntiformi, ereditando
 * colori e spessore del bordo dallo stile del layer. Senza simbolo
 * configurato applica il default (cerchio). `cross` e `x` sono linee:
 * renderizzano col solo stroke.
 */
export function createSymbolImage(style: IMapLayerStyle | undefined) {
  const radius = style?.symbol?.size ?? DefaultMapSymbolSize;
  const fill = new Fill({ color: style?.fillColor });
  const stroke = new Stroke({
    color: style?.strokeColor,
    width: style?.strokeWidth,
  });

  switch (style?.symbol?.shape) {
    case MapSymbolShape.Square:
      return new RegularShape({
        points: 4,
        radius,
        angle: Math.PI / 4,
        fill,
        stroke,
      });

    case MapSymbolShape.Triangle:
      return new RegularShape({ points: 3, radius, fill, stroke });

    case MapSymbolShape.Star:
      return new RegularShape({
        points: 5,
        radius,
        radius2: radius * 0.4,
        fill,
        stroke,
      });

    case MapSymbolShape.Cross:
      return new RegularShape({ points: 4, radius, radius2: 0, stroke });

    case MapSymbolShape.X:
      return new RegularShape({
        points: 4,
        radius,
        radius2: 0,
        angle: Math.PI / 4,
        stroke,
      });

    case MapSymbolShape.Circle:
    default:
      return new CircleStyle({ radius, fill, stroke });
  }
}
